import { ONE_NIGHT_CENTRE_CARDS, levelForXp } from '@ringmaster/shared';
import type {
  BonusKind,
  Phase,
  PlayerPublic,
  PrivateView,
  PublicView,
  RoleId,
} from '@ringmaster/shared';

/**
 * Canned view fixtures for EVERY phase × role, so UI agents can render any screen
 * statically without driving the MockDriver. Based on the BLOOD_MOON_FLOW.md
 * 6-player table (Ana host/Villager, Ben Wolf, Cara Seer, Dan/Eve/Finn Villagers).
 *
 * These are illustrative snapshots, not a running game — the MockDriver produces
 * the live sequence. Use whichever fits your screen.
 */

export const MOCK_CODE = 'RVNK';

export interface MockPlayer {
  id: string;
  name: string;
  role: RoleId;
  /**
   * Lifetime XP behind the level badge (`PlayerPublic.xp`). `null` = a seat with
   * no profile yet, which renders NO badge — the roster keeps one on purpose so
   * mock surfaces show both sides of the "no badge until your first game" rule.
   */
  xp: number | null;
  /**
   * The welcome grant the server froze onto this seat for the match under way
   * (`PlayerPublic.bonus`). Absent for the common case — a second game in one
   * sitting — which is why most of the roster below leaves it off.
   */
  bonus?: BonusKind | null;
}

/**
 * The scripted roster. Ids are stable so fixtures cross-reference cleanly.
 * XP is spread across four tiers (shared/levels.ts LEVEL_XP, one colour band per
 * five levels) so a mock roster shows badges of different colours side by side
 * rather than six of the same one.
 *
 * Two seats also carry a welcome grant, and they are the two the data already
 * implies: Finn has never finished a game (no XP, no badge) so his is the
 * once-ever `first_game`, and Cara — the seat every mock surface plays as — is
 * back after a break, so hers is the `daily` one. The rest are mid-sitting and
 * owed nothing, which is the case that has to look normal.
 */
export const MOCK_PLAYERS: MockPlayer[] = [
  { id: 'p_ana', name: 'Ana', role: 'VILLAGER', xp: 11200 }, // level 17 — verdigris
  { id: 'p_ben', name: 'Ben', role: 'WEREWOLF', xp: 2400 }, // level 6 — gold
  { id: 'p_cara', name: 'Cara', role: 'SEER', xp: 5600, bonus: 'daily' }, // level 11 — arcana
  { id: 'p_dan', name: 'Dan', role: 'VILLAGER', xp: 820 }, // level 3 — bronze
  { id: 'p_eve', name: 'Eve', role: 'VILLAGER', xp: 300 }, // exactly level 2, empty bar
  { id: 'p_finn', name: 'Finn', role: 'VILLAGER', xp: null, bonus: 'first_game' }, // first game — no badge
];

export const HOST_ID = 'p_ana';
const ROLE_MAP: Record<string, RoleId> = Object.fromEntries(
  MOCK_PLAYERS.map((p) => [p.id, p.role]),
);

function team(role: RoleId): 'VILLAGE' | 'WOLVES' {
  return role === 'WEREWOLF' ? 'WOLVES' : 'VILLAGE';
}

/**
 * Build a PlayerPublic roster. `dead` marks who has died; `reveal` forces full
 * reveal (END). Living players always have revealedRole null.
 */
function roster(opts: { dead?: string[]; reveal?: boolean } = {}): PlayerPublic[] {
  const dead = new Set(opts.dead ?? []);
  return MOCK_PLAYERS.map((p) => {
    const isDead = dead.has(p.id);
    return {
      id: p.id,
      name: p.name,
      alive: !isDead,
      connected: true,
      revealedRole: isDead || opts.reveal ? p.role : null,
      score: 0,
      // Lifetime progression. `level` is a pure function of `xp` on the server
      // too (shared/levels.ts) — derived here rather than hand-typed so the two
      // can never disagree in a fixture.
      xp: p.xp,
      level: p.xp == null ? null : levelForXp(p.xp),
      // Server-decided at match start and frozen (shared/state.ts) — carried
      // through so a surface built on these fixtures appends the same ceremony
      // row the live one would.
      bonus: p.bonus ?? null,
    };
  });
}

function basePublic(phase: Phase, over: Partial<PublicView> = {}): PublicView {
  return {
    code: MOCK_CODE,
    phase,
    round: 1,
    players: roster(),
    config: { discussionSecs: 180 },
    minPlayers: 5,
    hostId: HOST_ID,
    wakingRole: null,
    dawn: null,
    voteProgress: null,
    skipVotes: null,
    lynchResult: null,
    result: null,
    // Points are an END-only field (shared/scoring.ts) — the scripted mock never
    // reaches a scored ending, so every fixture row carries an empty ledger.
    awards: null,
    timerEndsAt: null,
    paused: false,
    timerRemainingMs: null,
    ...over,
  };
}

/**
 * A One Night public row: `gameId` set (so gameId-routed UI actually renders the
 * One Night surface rather than Blood Moon's) plus the PUBLIC card pool, which
 * is `players.length + ONE_NIGHT_CENTRE_CARDS` cards from DEAL onward — the
 * anchor the whole deduction hangs on, and what the persistent roles button
 * (ui/OneNightRolesButton.tsx) counts. `reveal` stays null: the full truth is an
 * END-only field.
 *
 * Declared ABOVE `publicFixtures` (with its pool const): the map below is built
 * at module load, so a `const` defined after it would be in its TDZ.
 */
function oneNightPublic(phase: Phase): PublicView {
  return basePublic(phase, {
    round: 1,
    gameId: 'onenight',
    oneNight: { pool: ONE_NIGHT_MOCK_POOL, centreCount: ONE_NIGHT_CENTRE_CARDS, reveal: null },
  });
}

/** 6 seats + 3 centre cards = 9 (the scripted roster's pool). */
const ONE_NIGHT_MOCK_POOL: RoleId[] = [
  'WEREWOLF',
  'WEREWOLF',
  'SEER',
  'THIEF',
  'VILLAGER',
  'VILLAGER',
  'VILLAGER',
  'VILLAGER',
  'VILLAGER',
];

// ─────────────────────────────────────────────────────────────────────────────
// PUBLIC VIEW FIXTURES (one per phase) — what the Stage shows
// ─────────────────────────────────────────────────────────────────────────────

export const publicFixtures: Record<Phase, PublicView> = {
  LOBBY: basePublic('LOBBY', { round: 0 }),
  DEAL: basePublic('DEAL', { timerEndsAt: Date.now() + 30_000 }),
  NIGHT_INTRO: basePublic('NIGHT_INTRO', {
    // Ambient nightfall pause — nobody wakes.
    timerEndsAt: Date.now() + 10_000,
  }),
  NIGHT_WOLVES: basePublic('NIGHT_WOLVES', {
    wakingRole: 'WEREWOLF',
    timerEndsAt: Date.now() + 40_000,
  }),
  NIGHT_INTERLUDE: basePublic('NIGHT_INTERLUDE', {
    // "Seer awaken" pause — nobody wakes yet.
    timerEndsAt: Date.now() + 6_000,
  }),
  NIGHT_SEER: basePublic('NIGHT_SEER', {
    wakingRole: 'SEER',
    timerEndsAt: Date.now() + 30_000,
  }),
  RESOLVE_NIGHT: basePublic('RESOLVE_NIGHT'),
  DAWN: basePublic('DAWN', {
    players: roster({ dead: ['p_eve'] }),
    dawn: { victimId: 'p_eve' },
  }),
  DISCUSS: basePublic('DISCUSS', {
    players: roster({ dead: ['p_eve'] }),
    timerEndsAt: Date.now() + 180_000,
  }),
  VOTE: basePublic('VOTE', {
    players: roster({ dead: ['p_eve'] }),
    voteProgress: { voted: 3, total: 5 },
    timerEndsAt: Date.now() + 30_000,
  }),
  RESOLVE_VOTE: basePublic('RESOLVE_VOTE', {
    players: roster({ dead: ['p_eve', 'p_dan'] }),
    lynchResult: {
      targetId: 'p_dan',
      tie: false,
      votes: {
        p_ana: 'p_dan',
        p_ben: 'p_dan',
        p_cara: 'p_ana',
        p_dan: 'p_ana',
        p_finn: 'p_dan',
      },
    },
  }),
  END: basePublic('END', {
    round: 2,
    players: roster({ dead: ['p_eve', 'p_dan', 'p_ben'], reveal: true }),
    result: {
      winner: 'VILLAGE',
      atDawn: false,
      roles: ROLE_MAP,
    },
  }),
  // ── The Whisper — placeholder fixtures (the MockDriver scripts Blood Moon; a
  //    scripted Whisper path is deferred to M6). Present only to satisfy the
  //    total Record<Phase, PublicView>; they mirror the LOBBY shape.
  //    TODO(whisper M6): a real scripted Whisper mock.
  WHISPER_INTRO: basePublic('WHISPER_INTRO', { round: 1 }),
  QUESTION: basePublic('QUESTION', { round: 1 }),
  RESOLVE_GUESS: basePublic('RESOLVE_GUESS', { round: 1 }),
  HUNT: basePublic('HUNT', { round: 1 }),
  RESOLVE_HUNT: basePublic('RESOLVE_HUNT', { round: 1 }),
  SCORE: basePublic('SCORE', { round: 1 }),
  // ── One Night — placeholder fixtures (the MockDriver scripts Blood Moon; a
  //    scripted One Night path is a future TODO, same as the Whisper). Present
  //    only to satisfy the total Record<Phase, PublicView>.
  ONENIGHT_NIGHTFALL: oneNightPublic('ONENIGHT_NIGHTFALL'),
  ONENIGHT_NIGHT_ACTIONS: oneNightPublic('ONENIGHT_NIGHT_ACTIONS'),
  ONENIGHT_DAWN: oneNightPublic('ONENIGHT_DAWN'),
  ONENIGHT_DISCUSS: oneNightPublic('ONENIGHT_DISCUSS'),
  ONENIGHT_VOTE: oneNightPublic('ONENIGHT_VOTE'),
  ONENIGHT_RESOLVE: oneNightPublic('ONENIGHT_RESOLVE'),
};

// ─────────────────────────────────────────────────────────────────────────────
// PRIVATE VIEW FIXTURES — keyed by phase then by archetype
// Archetypes: 'wolf' (Ben), 'seer' (Cara), 'villager' (Dan alive), 'dead' (Eve)
// ─────────────────────────────────────────────────────────────────────────────

export type PrivateArchetype = 'wolf' | 'seer' | 'villager' | 'dead';

const PLAYER_FOR: Record<PrivateArchetype, MockPlayer> = {
  wolf: MOCK_PLAYERS[1]!, // Ben
  seer: MOCK_PLAYERS[2]!, // Cara
  villager: MOCK_PLAYERS[3]!, // Dan
  dead: MOCK_PLAYERS[4]!, // Eve
};

function basePrivate(
  archetype: PrivateArchetype,
  phase: Phase,
  over: Partial<PrivateView> = {},
): PrivateView {
  const p = PLAYER_FOR[archetype];
  const alive = archetype !== 'dead';
  const view: PrivateView = {
    playerId: p.id,
    role: p.role,
    team: team(p.role),
    alive,
    packmates: null,
    packAgreement: null,
    action: { type: 'NONE' },
    seerResult: null,
    seerHistory: null,
    // The full roster is revealed ONLY at END; the dead do NOT learn everyone's
    // roles mid-game (matches engine views.ts). END fixtures override this
    // explicitly with { ...ROLE_MAP }.
    deadTruth: null,
    ...over,
  };
  return view;
}

/** Living non-wolf ids (wolf's candidate list at the DAWN-victim example). */
const LIVING_NON_WOLVES = ['p_ana', 'p_cara', 'p_dan', 'p_eve', 'p_finn'];
const LIVING_NON_SELF_SEER = ['p_ana', 'p_ben', 'p_dan', 'p_eve', 'p_finn'];
const LIVING_ALL = ['p_ana', 'p_ben', 'p_cara', 'p_dan', 'p_finn']; // after Eve dies

const wolfPackmatesSolo = null; // Ben is the only wolf at 6 players

export const privateFixtures: Record<Phase, Record<PrivateArchetype, PrivateView>> = {
  LOBBY: {
    wolf: basePrivate('wolf', 'LOBBY'),
    seer: basePrivate('seer', 'LOBBY'),
    villager: basePrivate('villager', 'LOBBY'),
    dead: basePrivate('dead', 'LOBBY', { alive: true, deadTruth: null }),
  },
  DEAL: {
    wolf: basePrivate('wolf', 'DEAL', {
      packmates: wolfPackmatesSolo,
      packAgreement: null,
      action: { type: 'READY', done: false },
    }),
    seer: basePrivate('seer', 'DEAL', { action: { type: 'READY', done: false } }),
    villager: basePrivate('villager', 'DEAL', { action: { type: 'READY', done: false } }),
    dead: basePrivate('dead', 'DEAL', {
      alive: true,
      deadTruth: null,
      action: { type: 'READY', done: false },
    }),
  },
  NIGHT_INTRO: {
    // Ambient pause — everyone sleeps (action NONE), including wolf & seer.
    wolf: basePrivate('wolf', 'NIGHT_INTRO', { packmates: wolfPackmatesSolo }),
    seer: basePrivate('seer', 'NIGHT_INTRO'),
    villager: basePrivate('villager', 'NIGHT_INTRO'),
    dead: basePrivate('dead', 'NIGHT_INTRO', { alive: true, deadTruth: null }),
  },
  NIGHT_WOLVES: {
    wolf: basePrivate('wolf', 'NIGHT_WOLVES', {
      packmates: wolfPackmatesSolo,
      packAgreement: null,
      action: { type: 'WOLF_PICK', candidates: LIVING_NON_WOLVES, myPick: 'p_eve' },
    }),
    seer: basePrivate('seer', 'NIGHT_WOLVES'),
    villager: basePrivate('villager', 'NIGHT_WOLVES'),
    dead: basePrivate('dead', 'NIGHT_WOLVES', { alive: true, deadTruth: null }),
  },
  NIGHT_INTERLUDE: {
    // Wolves have slept; seer not yet awake — everyone sleeps (action NONE).
    wolf: basePrivate('wolf', 'NIGHT_INTERLUDE', { packmates: wolfPackmatesSolo }),
    seer: basePrivate('seer', 'NIGHT_INTERLUDE'),
    villager: basePrivate('villager', 'NIGHT_INTERLUDE'),
    dead: basePrivate('dead', 'NIGHT_INTERLUDE', { alive: true, deadTruth: null }),
  },
  NIGHT_SEER: {
    wolf: basePrivate('wolf', 'NIGHT_SEER', { packmates: wolfPackmatesSolo }),
    seer: basePrivate('seer', 'NIGHT_SEER', {
      action: { type: 'SEER_PICK', candidates: LIVING_NON_SELF_SEER },
    }),
    villager: basePrivate('villager', 'NIGHT_SEER'),
    dead: basePrivate('dead', 'NIGHT_SEER', { alive: true, deadTruth: null }),
  },
  RESOLVE_NIGHT: {
    wolf: basePrivate('wolf', 'RESOLVE_NIGHT'),
    seer: basePrivate('seer', 'RESOLVE_NIGHT'),
    villager: basePrivate('villager', 'RESOLVE_NIGHT'),
    dead: basePrivate('dead', 'RESOLVE_NIGHT', { alive: true, deadTruth: null }),
  },
  DAWN: {
    wolf: basePrivate('wolf', 'DAWN'),
    seer: basePrivate('seer', 'DAWN', {
      seerResult: { targetId: 'p_finn', targetName: 'Finn', isWerewolf: false },
      seerHistory: [
        { round: 1, targetId: 'p_finn', targetName: 'Finn', isWerewolf: false },
      ],
    }),
    villager: basePrivate('villager', 'DAWN'),
    // Eve is now dead → spectator view, but the roster stays hidden until END.
    dead: basePrivate('dead', 'DAWN'),
  },
  DISCUSS: {
    wolf: basePrivate('wolf', 'DISCUSS'),
    seer: basePrivate('seer', 'DISCUSS', {
      seerHistory: [
        { round: 1, targetId: 'p_finn', targetName: 'Finn', isWerewolf: false },
      ],
    }),
    villager: basePrivate('villager', 'DISCUSS'),
    dead: basePrivate('dead', 'DISCUSS'),
  },
  VOTE: {
    wolf: basePrivate('wolf', 'VOTE', {
      action: { type: 'VOTE', candidates: LIVING_ALL, myVote: 'p_dan' },
    }),
    seer: basePrivate('seer', 'VOTE', {
      action: { type: 'VOTE', candidates: LIVING_ALL, myVote: null },
      seerHistory: [
        { round: 1, targetId: 'p_finn', targetName: 'Finn', isWerewolf: false },
      ],
    }),
    villager: basePrivate('villager', 'VOTE', {
      action: { type: 'VOTE', candidates: LIVING_ALL, myVote: 'ABSTAIN' },
    }),
    dead: basePrivate('dead', 'VOTE'),
  },
  RESOLVE_VOTE: {
    wolf: basePrivate('wolf', 'RESOLVE_VOTE'),
    seer: basePrivate('seer', 'RESOLVE_VOTE'),
    villager: basePrivate('villager', 'RESOLVE_VOTE'),
    dead: basePrivate('dead', 'RESOLVE_VOTE'),
  },
  END: {
    wolf: basePrivate('wolf', 'END', { deadTruth: { ...ROLE_MAP } }),
    seer: basePrivate('seer', 'END', {
      deadTruth: { ...ROLE_MAP },
      seerHistory: [
        { round: 1, targetId: 'p_finn', targetName: 'Finn', isWerewolf: false },
        { round: 2, targetId: 'p_ben', targetName: 'Ben', isWerewolf: true },
      ],
    }),
    villager: basePrivate('villager', 'END', { deadTruth: { ...ROLE_MAP } }),
    dead: basePrivate('dead', 'END', { deadTruth: { ...ROLE_MAP } }),
  },
  // ── The Whisper — placeholder private fixtures (see the public note above).
  //    TODO(whisper M6): a real scripted Whisper mock.
  WHISPER_INTRO: whisperFixtureRow('WHISPER_INTRO'),
  QUESTION: whisperFixtureRow('QUESTION'),
  RESOLVE_GUESS: whisperFixtureRow('RESOLVE_GUESS'),
  HUNT: whisperFixtureRow('HUNT'),
  RESOLVE_HUNT: whisperFixtureRow('RESOLVE_HUNT'),
  SCORE: whisperFixtureRow('SCORE'),
  // ── One Night — placeholder private fixtures (see the public note above).
  //    TODO(onenight): a real scripted One Night mock.
  ONENIGHT_NIGHTFALL: whisperFixtureRow('ONENIGHT_NIGHTFALL'),
  ONENIGHT_NIGHT_ACTIONS: whisperFixtureRow('ONENIGHT_NIGHT_ACTIONS'),
  ONENIGHT_DAWN: whisperFixtureRow('ONENIGHT_DAWN'),
  ONENIGHT_DISCUSS: whisperFixtureRow('ONENIGHT_DISCUSS'),
  ONENIGHT_VOTE: whisperFixtureRow('ONENIGHT_VOTE'),
  ONENIGHT_RESOLVE: whisperFixtureRow('ONENIGHT_RESOLVE'),
};

/** A placeholder archetype row for a phase with no dedicated mock script yet
 *  (Whisper, One Night). */
function whisperFixtureRow(phase: Phase): Record<PrivateArchetype, PrivateView> {
  return {
    wolf: basePrivate('wolf', phase),
    seer: basePrivate('seer', phase),
    villager: basePrivate('villager', phase),
    dead: basePrivate('dead', phase, { alive: true, deadTruth: null }),
  };
}

/** Full role map for convenience (spectator / END screens). */
export const MOCK_ROLE_MAP: Record<string, RoleId> = { ...ROLE_MAP };
